import type { OnboardingMessageDto, OnboardingState, WizardStepKey } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, ErrorText } from '../../../ui';
import { ActivityLoader, MessageBubble } from '../Onboarding';

/** Apre «Parliamone» (ADR 0063), con un testo già iniziato quando serve (es. «Correggimi» su un'ipotesi). */
const TalkContext = createContext<{ open: (draft?: string) => void }>({ open: () => undefined });
export const useTalk = () => useContext(TalkContext);

/** I messaggi di un passo: quelli scritti dal passo e le risposte del cervello a quelli (ADR 0063). */
export function stepMessages(
  messages: OnboardingMessageDto[],
  step: WizardStepKey,
): OnboardingMessageDto[] {
  let asked: string | undefined;
  return messages.filter((m) => {
    if (m.role === 'user') {
      asked = m.changes?.passo;
      return asked === step;
    }
    // I messaggi di prima non hanno la chiave: valgono per il passo del messaggio a cui rispondono.
    return m.changes?.chiave ? m.changes.chiave === step : asked === step;
  });
}

const seenKey = (tenant: string, step: WizardStepKey) => `bk:parliamone:${tenant}:${step}`;
const readSeen = (k: string) => {
  try {
    return Number(localStorage.getItem(k)) || 0;
  } catch {
    return 0;
  }
};
const writeSeen = (k: string, id: number) => {
  try {
    localStorage.setItem(k, String(id));
  } catch {
    /* il pallino resta: non è un dato che conta */
  }
};

/**
 * «Parliamone» (ADR 0063): un solo posto per parlare con il cervello, uguale su ogni passo. Un pulsante fisso in basso a
 * destra apre il pannello con i messaggi di questo passo; ciò che la persona scrive aggiorna il passo e quelli dopo.
 */
export function TalkProvider({
  step,
  title,
  missing,
  children,
}: {
  step: WizardStepKey;
  title: string;
  missing: string[];
  children: ReactNode;
}) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const [text, setText] = useState('');
  const input = useRef<HTMLTextAreaElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const state = useTenantQuery<OnboardingState>(['onboarding'], '/onboarding', {
    // Anche mentre il passo si riprogetta con la decisione appena presa (ADR 0082).
    refetchInterval: (d) => (d?.thinking || d?.riprogetto ? 1500 : false),
  });
  const s = state.data;
  const mine = useMemo(() => (s ? stepMessages(s.messages, step) : []), [s, step]);
  const shown = all ? (s?.messages ?? []).slice(-30) : mine;
  const lastReply = mine.filter((m) => m.role === 'assistant').at(-1)?.id ?? 0;
  const key = seenKey(tenant.id, step);
  const [seen, setSeen] = useState(() => readSeen(key));
  useEffect(() => setSeen(readSeen(key)), [key]);
  // Aperto, ciò che c'è è letto; chiuso, il pallino dice che il cervello ha risposto qui.
  useEffect(() => {
    if (open && lastReply > seen) {
      writeSeen(key, lastReply);
      setSeen(lastReply);
    }
  }, [open, lastReply, seen, key]);
  const unread = !open && lastReply > seen;

  const value = useMemo(
    () => ({
      open: (draft?: string) => {
        setOpen(true);
        setAll(false);
        if (draft !== undefined) setText(draft);
        setTimeout(() => {
          const el = input.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }, 50);
      },
    }),
    [],
  );

  const working = !!s?.thinking || !!s?.riprogetto;
  const wasWorking = useRef(false);
  useEffect(() => {
    // Quando il cervello ha risposto, e poi quando ha riprogettato il passo (ADR 0082), i dati dei passi sono cambiati:
    // si rileggono in background.
    if (wasWorking.current) void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    wasWorking.current = working;
  }, [s?.thinking, s?.riprogetto]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open)
      void bottom.current?.parentElement?.scrollTo({
        top: bottom.current.offsetTop,
        behavior: 'smooth',
      });
  }, [open, shown.length, s?.thinking, s?.riprogetto]);

  const send = useMutation({
    mutationFn: (message: string) =>
      api<OnboardingState>(t('/onboarding/messages'), { body: { message, passo: step } }),
    onSuccess: (next) => {
      setText('');
      qc.setQueryData(['tenant', tenant.id, 'onboarding'], next);
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const busy = !!s?.thinking || send.isPending;
  // Dopo la risposta il passo si riprogetta con la decisione (ADR 0082): il pannello lo mostra, poi dice cosa è cambiato.
  const replanning = !s?.thinking && !!s?.riprogetto;
  const last = mine.at(-1);
  const unchanged =
    last?.role === 'assistant' &&
    last.changes.riprogettato &&
    !last.changes.aggiornati?.some((u) => u.passo === step);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const m = text.trim();
    if (m && !busy) send.mutate(m);
  };

  return (
    <TalkContext.Provider value={value}>
      {children}
      {/* Lo spazio del pulsante fisso: in fondo alla pagina non copre «Avanti». */}
      <div className="h-16" aria-hidden />
      {isManager && !open && (
        <button
          onClick={() => value.open()}
          className="fixed bottom-4 right-4 z-30 flex items-center gap-2 rounded-full bg-accent px-4 py-3 text-sm font-medium text-white shadow-lg transition hover:bg-accent/90"
          aria-label={unread ? 'Parliamone: il cervello ti ha risposto' : 'Parliamone'}
        >
          <span aria-hidden>💬</span>
          Parliamone
          {unread && (
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300 ring-2 ring-white" aria-hidden />
          )}
        </button>
      )}
      {open && (
        <>
          {/* Sul telefono il pannello copre la pagina: toccare fuori lo chiude. */}
          <div
            className="fixed inset-0 z-30 bg-black/20 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <section
            role="dialog"
            aria-label="Parliamone"
            className="fixed inset-x-0 bottom-0 z-40 flex h-[80vh] flex-col rounded-t-2xl border border-black/10 bg-white shadow-2xl sm:inset-x-auto sm:bottom-4 sm:right-4 sm:top-20 sm:h-auto sm:w-[26rem] sm:rounded-2xl"
          >
            <header className="flex items-start gap-2 border-b border-black/5 px-4 py-3">
              <div className="flex-1">
                <h3 className="text-sm font-semibold">Parliamone</h3>
                <p className="text-xs text-ink/50">
                  Correggi o racconta: aggiorno questo passo e quelli dopo.
                </p>
              </div>
              <button
                className="rounded-lg px-2 py-1 text-ink/50 hover:bg-black/5 hover:text-ink"
                onClick={() => setOpen(false)}
                aria-label="Chiudi"
              >
                ✕
              </button>
            </header>
            <div className="flex-1 space-y-3 overflow-y-auto p-4">
              {!all && (
                <div className="rounded-xl bg-black/[0.03] px-3 py-2 text-sm text-ink/70">
                  <p>
                    Siamo in «{title}». Dimmi cosa non va o cosa manca, anche a parole tue: lo
                    sistemo qui e nei passi che ne dipendono.
                  </p>
                  {missing.length > 0 && (
                    <p className="mt-1.5 text-xs text-ink/55">
                      Mi aiuterebbe sapere: {missing.slice(0, 3).join('; ')}.
                    </p>
                  )}
                </div>
              )}
              {shown.map((m) => (
                <MessageBubble key={m.id} m={m} card={false} actions={m.id === lastReply} />
              ))}
              {busy && (
                <ActivityLoader
                  activity={s?.activity.turn ?? null}
                  fallback="Leggo la tua risposta…"
                />
              )}
              {replanning && (
                <ActivityLoader
                  activity={s?.activity.replan ?? null}
                  fallback="Riprogetto il passo con quello che mi hai detto…"
                />
              )}
              {!busy && !replanning && unchanged && (
                <p className="text-xs text-ink/50">
                  Ho riprogettato «{title}» con la tua decisione: le voci restano quelle di prima.
                </p>
              )}
              {s?.last_error && !s.thinking && (
                <p className="text-sm text-red-700">
                  Non sono riuscito a rispondere: {s.last_error}
                </p>
              )}
              {(s?.messages.length ?? 0) > mine.length && (
                <p className="text-center">
                  <button
                    className="text-xs text-ink/45 hover:text-accent hover:underline"
                    onClick={() => setAll((x) => !x)}
                  >
                    {all ? 'Solo questo passo' : 'Vedi tutta la conversazione'}
                  </button>
                </p>
              )}
              <div ref={bottom} />
            </div>
            <form className="border-t border-black/10 p-3" onSubmit={submit}>
              <ErrorText>{send.error ? errorMessage(send.error) : ''}</ErrorText>
              <div className="flex items-end gap-2">
                <textarea
                  ref={input}
                  className="max-h-32 min-h-[2.75rem] flex-1 resize-y rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent"
                  placeholder={
                    busy
                      ? 'Sto lavorando: aspetta la mia risposta…'
                      : 'Scrivi qui… (Invio per inviare)'
                  }
                  disabled={busy}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submit(e);
                    }
                  }}
                />
                <Button type="submit" disabled={!text.trim() || busy}>
                  Invia
                </Button>
              </div>
            </form>
          </section>
        </>
      )}
    </TalkContext.Provider>
  );
}
